import type { AppChannel, SlotId } from '@luminovo/apps-types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSlotAnalytics } from '../analytics/slotAnalytics';
import { fetchSlotAssignments, type SlotAssignments } from '../api/fetchSlotAssignments';
import { useSlotErrorReporting } from '../errors/slotErrorReporting';
import { useAppChannel } from './useAppChannel';

// The channel is part of the key, not a detail of the request underneath it: the
// two channels answer the same question about the same slot with different
// versions, and `staleTime: 60_000` means a shared entry would serve whichever
// response landed first to both. Navigating from a draft URL to the plain one
// would then keep showing the draft, which reads as the preview being stuck.
export const slotAssignmentsQueryKey = (slotId: SlotId, channel: AppChannel) =>
    ['app-platform', 'slot-assignments', slotId, channel] as const;

// Slot-and-channel pairs this page-session has already reported.
//
// Module-level, and that is the point: three separate call sites share one
// react-query cache for `slots.full-page` (FullPageSlots, the app layout, the
// command palette), and the hook remounts on every navigation. Per-hook dedupe
// would send the same fact several times per page view, for every page view.
//
// The fact being reported is "does this user's tenant have anything in this slot",
// which is stable for a session — so once is right, and the volume stays
// proportional to sessions rather than to navigations. The trade is that
// assignments changing mid-session go unreported until a reload.
//
// Keyed on the CHANNEL as well as the slot: the draft channel resolves a different
// set of versions and reports a count of its own, and a set keyed on the slot alone
// would report whichever channel the session saw first and silently drop the other.
const reportedSlots = new Set<string>();

// Failures already reported, keyed the same way and for the same reason.
const reportedFailures = new Set<string>();

const reportKey = (slotId: SlotId, channel: AppChannel) => `${slotId}:${channel}`;

/** Test seam — module-level state would otherwise leak between cases. */
export function resetReportedSlotsForTest(): void {
    reportedSlots.clear();
    reportedFailures.clear();
}

export interface SlotAssignmentsState extends SlotAssignments {
    // The fetch failed and `assignments` is an empty stand-in, not the tenant's answer. A page that only adds
    // to the product can ignore it; one that decides whether a URL exists must not read it as "no app here".
    degraded: boolean;
}

/** `enabled`: false while the page doesn't need the slot, as react-query's option: no request, and no data. */
export function useSlotAssignments(
    slotId: SlotId,
    { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<SlotAssignmentsState, Error> {
    const track = useSlotAnalytics();
    // Read here rather than taken as an argument, so every consumer of a slot on
    // one page is on the same channel without any of them having to know channels
    // exist. The alternative — a `channel` parameter — makes each call site a place
    // the product can accidentally split the page across two channels, and each
    // split costs a second request for a response the page already has.
    const channel = useAppChannel();
    const reportError = useSlotErrorReporting();
    const result = useQuery({
        queryKey: slotAssignmentsQueryKey(slotId, channel),
        queryFn: async ({ signal }) => {
            try {
                return { ...(await fetchSlotAssignments(slotId, { signal, channel })), degraded: false };
            } catch (error) {
                if (signal.aborted) {
                    throw error;
                }
                // Degrading to an empty slot keeps the page usable. `degraded` keeps it from
                // retiring every route an app owns: `FullPageSlots` shows its unavailable
                // state instead of the product's "page not found".
                const key = reportKey(slotId, channel);
                if (!reportedFailures.has(key)) {
                    reportedFailures.add(key);
                    reportError(
                        new Error(`[apps] slot assignments for ${slotId} could not be fetched`, { cause: error }),
                        { level: 'error', tags: { slot_id: slotId, channel } },
                    );
                }
                return { appsOrigin: undefined, assignments: [], degraded: true };
            }
        },
        // A stand-in is stale at once, so the next page that asks retries rather than serving the outage for a minute.
        staleTime: (query) => (query.state.data?.degraded ? 0 : 60_000),
        retry: false,
        enabled,
        placeholderData: (previousAssignments) => previousAssignments,
    });

    // The platform-adoption denominator: how many tenants have any app wired into
    // any surface at all. A count of zero is the interesting half of that, and is
    // reported like any other count, so a failed fetch's stand-in must not be.
    const assignmentCount = result.data?.degraded ? undefined : result.data?.assignments.length;
    useEffect(() => {
        const key = reportKey(slotId, channel);
        if (assignmentCount === undefined || reportedSlots.has(key)) {
            return;
        }
        reportedSlots.add(key);
        track('apps/slot_assignments_loaded', { slot: slotId, assignment_count: assignmentCount, channel });
    }, [assignmentCount, channel, slotId, track]);

    return result;
}
